import { z } from 'zod/v4';
import { CERTIFICATE_WORKFLOW_STATUS_VALUES } from '../../constants.js';

// PG `jsonb` columns are auto-parsed by the driver, so workflows/
// finishedWorkflows/metadata arrive as arrays/objects (not JSON strings).
// Keep a string fallback only for the legacy serialized form so older code
// paths that pre-serialized still work.
const jsonbStringArray = z
  .union([z.array(z.string()), z.string(), z.null()])
  .transform((v): string[] => {
    if (v == null) return [];
    if (Array.isArray(v)) return v;
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

const jsonbRecord = z
  .union([z.record(z.string(), z.unknown()), z.string(), z.null()])
  .transform((v): Record<string, unknown> => {
    if (v == null) return {};
    if (typeof v === 'object') return v as Record<string, unknown>;
    try {
      const parsed = JSON.parse(v);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });

export const CertificateWorkflowSchema = z.object({
  id: z.string(),
  fileId: z.string(),
  status: z.enum(CERTIFICATE_WORKFLOW_STATUS_VALUES),
  percentage: z.number(),
  workflows: jsonbStringArray,
  finishedWorkflows: jsonbStringArray,
  metadata: jsonbRecord,
  contentMd5Hash: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
});

export type ICertificateWorkflowDbRecord = z.input<
  typeof CertificateWorkflowSchema
>;

export type ICertificateWorkflow = z.infer<typeof CertificateWorkflowSchema>;

export const parseCertificateWorkflow = (
  record: ICertificateWorkflowDbRecord,
): ICertificateWorkflow => CertificateWorkflowSchema.parse(record);

export const CertificateAiWorkflowStatusSchema = z.object({
  id: z.string(),
  fileId: z.string(),
  status: z.string(),
  percentage: z.number(),
  createdAt: z.coerce.date().nullable(),
  updatedAt: z.coerce.date().nullable(),
});

export type ICertificateWorkflowStatus = z.infer<
  typeof CertificateAiWorkflowStatusSchema
>;

export const CertificateAiParamsSchema = z.object({
  fileId: z.string().min(1),
});

export const CertificateAiGetParamsSchema = z.object({
  workflowId: z.string().min(1),
});

const CertificateAiFileSchema = z.object({
  id: z.string(),
  name: z.string(),
  contentType: z.string().nullable(),
  fileSize: z.number().nullable(),
  url: z.string(),
});

export const CertificateAiWorkflowSchema = z.object({
  id: z.string(),
  fileId: z.string(),
  status: z.string(),
  percentage: z.number(),
  workflows: z.array(z.string()),
  finishedWorkflows: z.array(z.string()),
  metadata: z.record(z.string(), z.unknown()),
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().nullable(),
  file: CertificateAiFileSchema.nullable().optional(),
});
